import { useState } from 'react';
import {
  ChevronLeft, ChevronRight,
  Check,
} from 'lucide-react';
import CalendarSection from '../section/Calendar';
import MapPanel from '../section/MapPanel';
import { useCalendarJobs } from '../../Hooks/useCalendarJobs';
import { canAccess } from '../../utils/permissions';
import { Calendar as CalendarIcon } from 'lucide-react';

export default function Schedule() {
  const [currentDate, setCurrentDate] = useState(new Date(2025, 11, 21));
  const [viewMode, setViewMode] = useState('Month');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showMap, setShowMap] = useState(false);

  const { jobs, isLoading: jobsLoading } = useCalendarJobs();

  const goToPrevious = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (viewMode === 'Month') newDate.setMonth(newDate.getMonth() - 1);
      else if (viewMode === 'Week') newDate.setDate(newDate.getDate() - 7);
      else newDate.setDate(newDate.getDate() - 1);
      return newDate;
    });
  };

  const goToNext = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      if (viewMode === 'Month') newDate.setMonth(newDate.getMonth() + 1);
      else if (viewMode === 'Week') newDate.setDate(newDate.getDate() + 7);
      else newDate.setDate(newDate.getDate() + 1);
      return newDate;
    });
  };

  const goToToday = () => setCurrentDate(new Date());

  const formatHeaderDate = () => {
    if (viewMode === 'Month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } else if (viewMode === 'Week') {
      const start = new Date(currentDate);
      start.setDate(start.getDate() - start.getDay());
      const end = new Date(start);
      end.setDate(end.getDate() + 6);
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
  };

  return (
    <section className="min-h-screen flex flex-col text-sm">
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-gray-900">{formatHeaderDate()}</h1>

            </div>

            <div className="flex items-center gap-1">
              <button onClick={goToPrevious} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={goToNext} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronRight className="w-4 h-4" /></button>
              <button onClick={goToToday} className="px-4 py-1.5 bg-gray-900 text-white text-xs rounded-lg font-medium">Today</button>
            </div>


          </div>

          <div className="flex items-center gap-2">
            <div className="flex bg-gray-100 rounded-lg p-0.5">
              {['Month', 'Week', 'Day'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${viewMode === mode ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'}`}
                >
                  {mode}
                </button>
              ))}
            </div>



            <div className="relative">

              {showMoreMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowMoreMenu(false)} />
                  <div className="absolute right-0 top-full mt-1.5 w-60 bg-white rounded-xl shadow-xl border border-gray-200 z-20 text-xs">
                    {['Reschedule & reassign', 'Create new visits', 'Shift day vertical', 'Hide weekends', 'Schedule Settings'].map((item) => (
                      <button key={item} className="w-full text-left px-4 py-2.5 hover:bg-gray-50 flex items-center justify-between text-xs">
                        <span className="font-medium text-gray-700">{item}</span>
                        {item === 'Hide weekends' && <Check className="w-4 h-4 text-emerald-600" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>


      </div>

      <div className="flex-1 flex overflow-hidden">
        {!canAccess("view_schedule") ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center bg-gray-50">
            <div className="w-20 h-20 bg-red-50 rounded-full flex items-center justify-center mb-6">
              <CalendarIcon className="w-10 h-10 text-red-600" />
            </div>
            <h2 className="text-3xl font-black text-gray-900 mb-2 tracking-tight">Access Restricted</h2>
            <p className="text-gray-500 max-w-md font-medium">
              You don't have the required permissions to view the calendar schedule.
            </p>
          </div>
        ) : (
          <CalendarSection
            currentDate={currentDate}
            viewMode={viewMode}
            jobs={jobs}
            isLoading={jobsLoading}
          />
        )}



        {showMap && (
          <MapPanel
            jobsInView={jobs || []}
            onClose={() => setShowMap(false)}
          />
        )}
      </div>
    </section>
  );
}